import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { LogIn, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the page user was trying to access before being redirected to login
  const from = (location.state as any)?.from || '/';

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      if (!supabase) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Redirect to the intended destination or home
        navigate(from, { replace: true });
      }
    };

    checkAuth();
  }, [navigate, from]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supabase) {
      setError('Authentication service is not available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        console.log('Auth successful for user:', data.user.email);

        // Check if user has admin role
        const { data: profile, error: profileError } = await supabase
          .from('app_users')
          .select('role')
          .eq('email', email)
          .single();

        console.log('Profile query result:', { profile, profileError });

        if (profileError) {
          console.error('Profile error:', profileError);
          await supabase.auth.signOut();
          throw new Error(`Database error: ${profileError.message}. User may not be in app_users table.`);
        }

        if (!profile) {
          await supabase.auth.signOut();
          throw new Error('User profile not found in database. Please contact administrator.');
        }

        // Allow all valid roles (admin, user, readonly)
        // Granular permissions are enforced by the routing/page system
        if (!['admin', 'editor', 'manager', 'user', 'readonly'].includes(profile.role)) {
          console.log('User role:', profile.role);
          await supabase.auth.signOut();
          throw new Error(`Access denied. Invalid role: ${profile.role}. Contact administrator.`);
        }

        console.log('Login successful, redirecting to:', from);
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supabase) {
      setResetError('Authentication service is not available');
      return;
    }

    const targetEmail = (resetEmail || email).trim();
    if (!targetEmail) {
      setResetError('Enter an email address to receive the reset link.');
      return;
    }

    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    try {
      const basePath = import.meta.env.BASE_URL ?? '/';
      const trimmedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
      const redirectPath = `${trimmedBase || ''}/reset-password`;
      const redirectTo = `${window.location.origin}${redirectPath}`;

      const { error } = await supabase.auth.resetPasswordForEmail(targetEmail, {
        redirectTo
      });

      if (error) throw error;

      setResetMessage('Password reset email sent. Check your inbox for further instructions.');
    } catch (err: any) {
      setResetError(err.message || 'Failed to send reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900 rounded-lg border border-slate-800 p-8 shadow-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">RahsMatch Admin</h1>
            <p className="text-slate-400">Sign in to access the control center</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-800 rounded-md p-3 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="george.mcpherson@rightawaygroup.com"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 pr-10 bg-slate-800 border border-slate-700 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-slate-400 hover:text-slate-300" />
                  ) : (
                    <Eye className="h-4 w-4 text-slate-400 hover:text-slate-300" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-slate-900 text-slate-400">Or continue with</span>
            </div>
          </div>

          {/* Microsoft OAuth Button */}
          <button
            type="button"
            onClick={async () => {
              if (!supabase) return;
              setLoading(true);
              try {
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'azure',
                  options: {
                    scopes: 'email profile openid',
                    redirectTo: `${window.location.origin}/`
                  }
                });
                if (error) throw error;
              } catch (err: any) {
                setError(err.message || 'Microsoft sign-in failed');
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-50 disabled:bg-slate-700 disabled:cursor-not-allowed text-gray-900 font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 border border-gray-300"
          >
            <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none">
              <path d="M1 1h10v10H1z" fill="#f25022"/>
              <path d="M12 1h10v10H12z" fill="#00a4ef"/>
              <path d="M1 12h10v10H1z" fill="#ffb900"/>
              <path d="M12 12h10v10H12z" fill="#7fba00"/>
            </svg>
            Sign in with Microsoft
          </button>

          {/* Password reset */}
          <div className="mt-6 text-center">
            {!resetting ? (
              <button
                type="button"
                onClick={() => {
                  setResetting(true);
                  setResetEmail(email);
                  setResetError('');
                  setResetMessage('');
                }}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Forgot password?
              </button>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 text-left mb-1">
                    Send reset link to
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="you@example.com"
                    disabled={resetLoading}
                    required
                  />
                </div>
                {resetError && (
                  <p className="text-xs text-red-400 text-left">{resetError}</p>
                )}
                {resetMessage && (
                  <p className="text-xs text-emerald-400 text-left">{resetMessage}</p>
                )}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setResetting(false);
                      setResetError('');
                      setResetMessage('');
                      setResetEmail('');
                    }}
                    className="text-xs text-slate-400 hover:text-slate-300"
                  >
                    Back to sign in
                  </button>
                  <button
                    type="submit"
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors disabled:bg-slate-700"
                    disabled={resetLoading}
                  >
                    {resetLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-800">
            <p className="text-center text-xs text-slate-500">
              Authorized personnel only. All access is logged and monitored.
            </p>
          </div>
        </div>

        {/* Back to Main Site */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-sm text-slate-400 hover:text-slate-300 transition-colors"
          >
            ← Back to RahsMatch.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
